import type { MouseEvent } from "react";

import type { Book, PFPosition, Token } from "../api/types";
import {
  usePFAwaitOutcome,
  usePFBook,
  usePFCancelMarket,
  usePFOrders,
  usePFPlace,
  usePFPositions,
  usePFStatus,
} from "../hooks/usePredict";
import { format, mul, parse } from "../lib/money";
import type { SellFraction } from "../lib/position";
import { pfOutcomeText, pfQuick, type PFQuick, type PFQuickAction } from "../lib/predict";
import { Act, outcomeColor, shortPrice, WIDTH, type Detail } from "./QuickTable";

/**
 * Строка PF в быстрых действиях: кошелёк пробы predict.fun (ADR-0040).
 *
 * Колонки — те же исходы, что у аккаунтов Polymarket, но цены и доли — по
 * стакану predict.fun: у площадок свои книги, и кнопка с ценой Polymarket
 * ставила бы ордер не туда, куда смотрит оператор.
 *
 * Клавиш у строки нет намеренно: одна клавиша не должна тратить деньги на
 * двух площадках, а активная строка — всегда Polymarket.
 */

type Echo = (text: string, tone: "ok" | "bad" | "muted") => void;

const BUY_JOIN: PFQuickAction = { side: "BUY", mode: "join" };
const BUY_CROSS: PFQuickAction = { side: "BUY", mode: "cross" };
const SELL_JOIN: PFQuickAction = { side: "SELL", mode: "join" };
const SELL_CROSS: PFQuickAction = { side: "SELL", mode: "cross" };

/** Исход predict.fun для колонки: по имени, иначе по порядку (Up, Down). */
function outcomeOf(t: Token, i: number): "Up" | "Down" {
  const n = t.outcome.trim().toLowerCase();
  if (n === "up") return "Up";
  if (n === "down") return "Down";
  return i === 0 ? "Up" : "Down";
}

export function PFQuickRow({
  slug,
  tokens,
  detail,
  usd,
  fraction,
  onEcho,
}: {
  slug: string;
  tokens: Token[];
  detail: Detail;
  usd: string | null;
  fraction: SellFraction;
  onEcho: Echo;
}) {
  const status = usePFStatus();
  const st = status.data;
  const book = usePFBook(slug).data?.book ?? null;
  const positions = usePFPositions(slug, Boolean(st?.address)).data?.positions ?? [];
  const open = (usePFOrders(Boolean(st?.address)).data?.orders ?? []).filter((o) => o.slug === slug);
  const place = usePFPlace();
  const awaitOutcome = usePFAwaitOutcome();
  const cancelMarket = usePFCancelMarket();
  const digits = 2;

  const reason = !st
    ? "сервис predictfun не отвечает"
    : !st.trading
      ? `торговля ${st.trading_note}`
      : !book
        ? "нет стакана predict.fun"
        : book.trading_status !== "OPEN"
          ? `рынок ${book.trading_status}`
          : null;

  async function send(a: PFQuickAction, outcome: "Up" | "Down", q: PFQuick, at: number) {
    if (!q.ok) return;
    const head = `PF · ${a.side === "BUY" ? "купить" : "продать"} ${outcome.toUpperCase()} ${a.mode === "join" ? "M" : "T"}`;
    onEcho(`${head} ${q.price} × ${q.size} — отправляется…`, "muted");
    try {
      const r = await place.mutateAsync({
        slug,
        outcome,
        side: a.side,
        price: q.price,
        size: q.size,
        post_only: q.postOnly,
      });
      // Ответ — сразу после приёма площадкой; кнопки уже свободны, итог
      // дописывается вторым эхо (ADR-0041).
      onEcho(`${head}: принят, проверяется… (${r.order_id}) · ${Math.round(performance.now() - at)} мс`, "muted");
      const o = await awaitOutcome(r.order_id);
      const t = pfOutcomeText(o);
      onEcho(`${head}: ${t.text} · ${Math.round(performance.now() - at)} мс`, t.tone);
    } catch (e) {
      onEcho(`${head}: ${e instanceof Error ? e.message : String(e)}`, "bad");
    }
  }

  async function cancelAll() {
    try {
      const r = await cancelMarket.mutateAsync(slug);
      if (r.filled.length > 0) {
        onEcho(
          `PF: снято ${r.removed.length}, исполнились до снятия — ${r.filled.length} (${r.filled.join(", ")}): проверьте позицию`,
          "bad",
        );
      } else {
        onEcho(r.removed.length > 0 ? `PF: снято ордеров — ${r.removed.length}` : "PF: снимать нечего", "ok");
      }
    } catch (e) {
      onEcho(`PF · снять: ${e instanceof Error ? e.message : String(e)}`, "bad");
    }
  }

  const label = (
    <span className="flex flex-col">
      <span
        className="font-medium"
        title={
          st
            ? `predict.fun · ${st.network} · ${st.wallet_kind === "predict" ? "аккаунт" : "EOA"} ${st.address ?? "без ключа"}` +
              (st.balance_usdt ? ` · ${st.balance_usdt} USDT` : "")
            : "predict.fun"
        }
      >
        PF
      </span>
      {reason ? (
        <span className="text-[10px] text-amber-300">{reason}</span>
      ) : (
        st?.balance_usdt && (
          <span className="num text-[10px] text-[var(--color-muted)]">${cents(st.balance_usdt)}</span>
        )
      )}
    </span>
  );

  return (
    <tr className="bg-white/[0.02]">
      <td className="border-t border-[var(--color-border)] py-0.5 pr-2 pl-3 align-top">{label}</td>
      {tokens.map((t, i) => {
        const outcome = outcomeOf(t, i);
        const ob: Book | null = book ? (outcome === "Up" ? book.up : book.down) : null;
        const pos = positions.find((p) => p.outcome === outcome);
        const held = pos ? parse(pos.shares) : null;
        const toSell = held !== null && held > 0 ? sellShares(held, fraction) : null;
        const color = outcomeColor(i);
        const buy = (a: PFQuickAction) => pfQuick(a, ob, { usd }, st?.max_order_usdt);
        const sell = (a: PFQuickAction) => pfQuick(a, ob, { shares: toSell }, st?.max_order_usdt);
        return [
          <td key={`${t.asset_id}:b`} className="border-t border-l border-[var(--color-border)] px-2 py-0.5">
            <span className="flex gap-1">
              {[BUY_JOIN, BUY_CROSS].map((a) => (
                <PFButton
                  key={a.mode}
                  a={a}
                  q={buy(a)}
                  outcome={outcome}
                  detail={detail}
                  digits={digits}
                  color={color}
                  blocked={reason ?? (place.isPending ? "отправка предыдущего ордера" : null)}
                  onClick={(q, e) => void send(a, outcome, q, e.timeStamp || performance.now())}
                />
              ))}
            </span>
          </td>,
          <td
            key={`${t.asset_id}:p`}
            className="border-t border-[var(--color-border)] px-2 py-0.5 text-right"
          >
            <PFPositionView pos={pos} detail={detail} />
          </td>,
          <td key={`${t.asset_id}:s`} className="border-t border-[var(--color-border)] px-2 py-0.5">
            {toSell !== null && (
              <span className="flex gap-1">
                {[SELL_JOIN, SELL_CROSS].map((a) => (
                  <PFButton
                    key={a.mode}
                    a={a}
                    q={sell(a)}
                    outcome={outcome}
                    detail={detail}
                    digits={digits}
                    color="var(--color-sell)"
                    blocked={reason ?? (place.isPending ? "отправка предыдущего ордера" : null)}
                    onClick={(q, e) => void send(a, outcome, q, e.timeStamp || performance.now())}
                  />
                ))}
              </span>
            )}
          </td>,
        ];
      })}
      <td className="border-t border-l border-[var(--color-border)] py-0.5 pl-2 text-right">
        <Act
          detail="compact"
          mode={open.length > 0 ? `✕${open.length}` : "✕"}
          color="var(--color-sell)"
          disabled={!st?.trading || cancelMarket.isPending}
          title={
            st?.trading
              ? `снять ордера predict.fun по этому окну${open.length > 0 ? ` (открытых: ${open.length})` : ""}`
              : `торговля ${st?.trading_note ?? "недоступна"}`
          }
          onClick={() => void cancelAll()}
        />
      </td>
    </tr>
  );
}

function PFButton({
  a,
  q,
  outcome,
  detail,
  digits,
  color,
  blocked,
  onClick,
}: {
  a: PFQuickAction;
  q: PFQuick;
  outcome: "Up" | "Down";
  detail: Detail;
  digits: number;
  color: string;
  blocked: string | null;
  onClick: (q: PFQuick, e: MouseEvent) => void;
}) {
  const letter = a.mode === "join" ? "M" : "T";
  // В узкой раскладке цены стоят в шапке колонки — но это цены Polymarket.
  // Кнопка PF без своей цены читалась бы по ним, поэтому цена predict.fun —
  // прямо на кнопке.
  const mode =
    detail === "full"
      ? a.mode === "join"
        ? "Maker"
        : "Taker"
      : detail === "compact" && q.ok
        ? `${letter} ${shortPrice(q.price, digits)}`
        : letter;
  const head = `PF · ${a.side === "BUY" ? "купить" : "продать"} ${outcome.toUpperCase()} ${
    a.mode === "join" ? "мейкером (post-only)" : "тейкером"
  }`;
  const title = blocked
    ? `${head}: ${blocked}`
    : q.ok
      ? `${head} ${q.price} × ${q.size} долей ≈ ${q.notional} USDT`
      : `${head}: ${q.reason}`;
  return (
    <Act
      detail={detail}
      mode={mode}
      price={q.ok ? shortPrice(q.price, digits) : "—"}
      sub={q.ok ? (detail === "full" ? `${q.size} sh ≈ $${cents(q.notional)}` : `${q.size} sh`) : ""}
      tail=""
      width={a.side === "BUY" ? WIDTH[detail].buy : WIDTH[detail].close}
      color={color}
      filled={a.side === "BUY"}
      disabled={blocked !== null || !q.ok}
      title={title}
      onClick={(e) => onClick(q, e)}
    />
  );
}

function PFPositionView({ pos, detail }: { pos: PFPosition | undefined; detail: Detail }) {
  const shares = pos ? parse(pos.shares) : null;
  return (
    <span
      className="num inline-block overflow-hidden text-right whitespace-nowrap"
      style={{ width: WIDTH[detail].pos }}
      title={
        pos && shares
          ? `${pos.shares} долей predict.fun${pos.avg_price ? ` по средней ${pos.avg_price}` : ""}` +
            (pos.value_usd ? ` · стоимость $${pos.value_usd}` : "")
          : undefined
      }
    >
      {pos && shares ? (
        <>
          {format(Math.floor(shares / 100) * 100)}
          {pos.avg_price && (
            <span className="text-[var(--color-muted)]"> @{shortPrice(pos.avg_price, 3)}</span>
          )}
        </>
      ) : (
        <span className="text-[var(--color-muted)]">—</span>
      )}
    </span>
  );
}

/** Доля позиции к продаже: fraction % от долей, строкой. */
function sellShares(held: number, fraction: SellFraction): string {
  const part = fraction === 100 ? held : mul(held, (fraction / 100) * 1_000_000);
  return format(part ?? held);
}

function cents(usd: string): string {
  const v = parse(usd);
  return v === null ? usd : (Math.floor(v / 10_000) / 100).toFixed(2);
}
